import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageSquare, Loader2, Send } from "lucide-react";

/**
 * MessageButton — opens a compose dialog and starts / continues a conversation with recipient.
 * Not shown if the current user is the recipient (self-messaging is blocked).
 */
export default function MessageButton({
  recipientId,
  recipientName = "member",
  targetType,
  targetId,
  targetTitle,
  className = "",
  size = "md",
  variant = "primary",
}) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  if (!recipientId || (user && user.id === recipientId)) return null;

  const click = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to send a message");
      nav("/login");
      return;
    }
    setOpen(true);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return toast.error("Write a message first");
    setBusy(true);
    try {
      const { data } = await api.post("/messages/start", {
        recipient_id: recipientId,
        body: body.trim(),
        target_type: targetType || null,
        target_id: targetId || null,
        target_title: targetTitle || "",
      });
      toast.success("Message sent");
      setOpen(false);
      setBody("");
      nav(`/inbox/${data.conversation.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  const btnClass = variant === "primary"
    ? "abm-btn-primary rounded-full"
    : "rounded-full border border-[#1E3A2F] text-[#1E3A2F] hover:bg-[#1E3A2F] hover:text-white transition";
  const sizeClass = size === "sm" ? "h-9 px-4 text-sm" : "h-11 px-5";

  return (
    <>
      <button
        type="button"
        onClick={click}
        data-testid={`message-btn-${recipientId}`}
        className={`inline-flex items-center gap-2 ${btnClass} ${sizeClass} ${className}`}
      >
        <MessageSquare className="w-4 h-4" />
        Message
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A2F] flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#C05A3D]" /> Message {recipientName}
            </DialogTitle>
            <DialogDescription>
              {targetTitle ? <>About <span className="italic">"{targetTitle}"</span>. </> : null}
              Your email stays private — replies land in your ABM inbox.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={send} className="space-y-4">
            <div>
              <Label htmlFor="message-body" className="text-sm text-[#1E3A2F]">Your message</Label>
              <Textarea
                id="message-body"
                data-testid="message-body-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Hi! I'm interested in this — is it still available?"
                className="mt-1 min-h-[120px]"
                maxLength={2000}
                autoFocus
              />
              <div className="text-xs text-[#5C5C5C] mt-1 text-right">{body.length}/2000</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="message-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={busy || !body.trim()} data-testid="message-send-btn" className="abm-btn-primary rounded-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
